import { Grid, Segment } from '@mairistem/composants';
import React from 'react';
import ShowOnly from '../../constants/feuilleSaisie/showOnly';
import type TypePeriode from '../../constants/simulation/typePeriode';
import type TreeRow from '../../interfaces/feuilleSaisie/treeRow';
import type ApercuNotePresentation from '../../interfaces/simulation/apercuNotePresentation';
import type Filters from '../../interfaces/simulation/filters';
import type Simulation from '../../interfaces/simulation/simulation';
import { getSimulation } from '../../services/simulation/simulation';
import {
	fetchIsDemandeEnabled,
	getDefaultPeriode,
	handleChangeMontantToSimulation,
} from '../../utils/simulation/simulation';
import { setSimulationCourante } from '../common/Context/simulationStore';
import { useUserRoleCourant } from '../common/Context/userRoleStore';
import TreeView from '../feuilleSaisie/TreeView';
import BoiteOutil from './BoiteOutil';
import './ElaborationBudget.css';
import ElaborationBudgetPlaceholder from './ElaborationBudgetPlaceholder';
import NotePresentationViewer from './Outils/NotePresentation/NotePresentationViewer';
import Panels from './Panels/Panels';
import SimulationAction from './SimulationAction';

interface ElaborationBudgetProps {
	identifiantSimulation: number;
}

const ElaborationBudget = ({
	identifiantSimulation,
}: ElaborationBudgetProps) => {
	const userRole = useUserRoleCourant();
	const [apercuNote, setApercuNote] =
		React.useState<ApercuNotePresentation>(null);
	const [filters, setFilters] = React.useState<Filters>();
	const [isDemandeEnabled, setIsDemandeEnabled] = React.useState(false);
	const [simulation, setSimulation] = React.useState<Simulation>(null);

	const isModuleDemandeEnabled = async (newSimulation: Simulation) => {
		const shouldShowDemande = await fetchIsDemandeEnabled(newSimulation);

		setIsDemandeEnabled(shouldShowDemande);

		return shouldShowDemande;
	};

	const fetchSimulation = async (
		idSimulation: number,
		onlyPanelData: boolean,
		typePeriode: TypePeriode,
	) => {
		const s = await getSimulation(
			idSimulation,
			new URLSearchParams({
				withBudget: 'true',
				withDM: 'true',
				withEntite: 'true',
				withImputationsSimulation: 'true',
				withTypePlan: 'true',
			}),
		);
		const moduleDemandeEnabled = await isModuleDemandeEnabled(s);

		// Remise a zero des filtres
		setFilters((old) => ({
			...old,
			showOnly: ShowOnly.TOUTES_LIGNES,
		}));

		if (onlyPanelData) {
			setSimulation((old) => ({
				...old,
				panelData: s.panelData,
			}));
		} else {
			setSimulation({
				...s,
				typePeriode: typePeriode ?? getDefaultPeriode(s, moduleDemandeEnabled),
			});
		}
	};

	const handleRefreshSimulation = (
		onlyPanelData = false,
		typePeriode: TypePeriode = null,
	) => {
		fetchSimulation(Number(identifiantSimulation), onlyPanelData, typePeriode);
	};

	// Chargement de la simulation
	// biome-ignore lint/correctness/useExhaustiveDependencies: pas besoin de 'handleRefreshSimulation'
	React.useEffect(() => {
		handleRefreshSimulation();
	}, []);

	// Partage de la simulation (et de la periode affichee) avec l'arbitrage de
	// masse, qui n'a ainsi pas a la recharger
	React.useEffect(() => {
		if (simulation) setSimulationCourante(simulation);
	}, [simulation]);

	const handleChangeMontant = (
		differencesMontants: any,
		imputation: TreeRow,
	) => {
		handleChangeMontantToSimulation(
			differencesMontants,
			imputation,
			simulation,
			setSimulation,
		);
	};

	const handleChangeSimulation = (data: Simulation) => {
		setSimulation((old) => ({ ...old, ...data }));
	};

	const handleFermerApercuNote = () => setApercuNote(null);

	return simulation ? (
		<>
			<Segment
				basic
				className={apercuNote ? 'elaborationBudgetMasquee' : undefined}
				secondary
			>
				<Grid stretched>
					<Grid.Row>
						<Grid.Column width={4}>
							<SimulationAction
								isDemandeEnabled={isDemandeEnabled}
								onRefresh={handleRefreshSimulation}
								onSave={handleChangeSimulation}
								simulation={simulation}
								userRole={userRole}
							/>
						</Grid.Column>
						<Grid.Column width={12}>
							<BoiteOutil
								onApercuNote={setApercuNote}
								onRefresh={handleRefreshSimulation}
								simulation={simulation}
								userRole={userRole}
							/>
						</Grid.Column>
					</Grid.Row>
				</Grid>

				<Panels simulation={simulation} />

				<TreeView
					filters={filters}
					onChangeMontant={handleChangeMontant}
					onRefresh={handleRefreshSimulation}
					setFilters={setFilters}
					simulation={simulation}
					userRole={userRole}
					isDemandeEnabled={isDemandeEnabled}
				/>
			</Segment>
			{apercuNote && (
				<NotePresentationViewer
					apercu={apercuNote}
					onClose={handleFermerApercuNote}
				/>
			)}
		</>
	) : (
		<ElaborationBudgetPlaceholder />
	);
};

export default ElaborationBudget;
